import {
  LIVE_GRANT_PROTOCOL_PREFIX,
  LIVE_PING_EVERY_MS,
  LIVE_PROTOCOL,
  LIVE_RENEWAL_GRACE_MS,
  type LiveChangedBy,
  type LiveClientMessage,
  type LiveGrantClaims,
  liveCloseCodes,
  liveGrantClaims,
  liveMessage,
  liveRowId,
  type PresentPerson,
  REVOCATION_MARGIN_MS,
  TYPING_EVERY_MS,
  TYPING_MS,
} from "@nexxt/contracts/realtime";

// The sockets of Nexxt Live in the browser (ADR-278): one per room a screen listens to, opened with the grant the read
// of that room answered with (ADR-277). A socket brings ids, never data: on `changed` the screens of the room read
// again from the gateway. A socket that does not open, or drops, is tried again after a backoff, and meanwhile the
// screens of the room read again every minute. A room that never had a grant (`LIVE` off) does neither: the screens
// are as they were before Nexxt Live. A screen that shows who is here joins the room's presence: the room hears where
// the user is, and tells who else is there (ADR-279), and who made a change, kept for the screens that tint the rows it
// changed (ADR-308). The user's own room (`OWN_ROOM`) needs no grant, the session is its access: it says what to read
// again for this user alone, the bell (ADR-280). No React here: `LiveProvider` binds it to the queries.

/** The room of the signed-in user (`/live/me`, ADR-277): opened with the session, and told `notified`. */
export const OWN_ROOM = "me";

/** What the client does with the socket of a room: the browser's WebSocket, or a fake in the tests. */
export interface LiveSocket {
  send(data: string): void;
  close(): void;
}

export interface LiveSocketEvents {
  open(): void;
  message(data: unknown): void;
  close(code: number): void;
}

/** Opens the socket of `room` offering `protocols`: the browser's `new WebSocket`, wired to `on`. */
export type LiveConnect = (room: string, protocols: string[], on: LiveSocketEvents) => LiveSocket;

/** Who listens to a room: a screen, which reads again what it shows of it. */
export interface RoomListener {
  /**
   * Read again. `signalled`: the room said something changed, so a read that finds nothing new may have been answered
   * by a replica behind (Q-SEC-33, point 4); otherwise a change may have come while the socket was not there.
   */
  changed(signalled: boolean): void;
}

/**
 * While the socket of a room that had one is not open, its screens read again this often (ADR-278); with it open, a
 * query that waits for something reads no more often than this (`pollUnlessLive`, ADR-285).
 */
export const LIVE_RESERVE_POLL_MS = 60_000;

/** A grant ending sooner than this is not offered: the upgrade would race its end. A read gives a new one. */
const GRANT_SLACK_MS = 30_000;
/** A read older than this when the socket opens may have missed a change: the screens read again once. */
const CATCH_UP_AFTER_MS = 2_000;
/** Signals of one room this close together are read once: a bulk write, a few saves in a row. */
const COALESCE_MS = 200;
/** A socket that was open and dropped (a release of `realtime`, the network) is opened again after about this. */
const DROP_DELAY_MS = 1_000;
/**
 * Every tab of every user has its own room open (the bell, ADR-280): after a release of `realtime` they open again
 * spread over this many seconds more, not all in the same second.
 */
const OWN_ROOM_SPREAD_MS = 4_000;
/**
 * A socket that did not open, or was refused (`4403`), waits beyond the margin of a revocation before the screens read
 * again for a new grant (ADR-277, point 6): a read sooner would give a grant the user room still refuses.
 */
export const REFUSED_DELAY_MS = REVOCATION_MARGIN_MS + 1_000;
const MAX_DELAY_MS = 5 * 60_000;
/** A socket open this long was admitted by the room: a refused one is closed right after it opens. */
const ADMITTED_AFTER_MS = 1_000;
/** The room keeps when it last answered: a tab silent past a ping and a half leaves the presence (ADR-310). */
const PING_EVERY_MS = LIVE_PING_EVERY_MS;
/** A socket admitted this long ago that drops is a drop, not a room that keeps failing: the backoff starts again. */
const STABLE_MS = 30_000;
/** No `pong` this long after a ping: the socket is gone without a close (a sleeping laptop, a lost network). */
const PONG_WITHIN_MS = 10_000;
/** The writes of this tab whose signals it does not read again: the mutation already did. */
const OWN_WRITES = 100;
/**
 * After the room closed a socket at the end of its grant, the tab keeps who was there this long at most (ADR-310): the
 * room's own grace and a few seconds more. A renewal that does not come back (no grant: the access is gone, Nexxt Live
 * is off, the read failed) then shows nobody.
 */
const RENEWAL_PRESENCE_MS = LIVE_RENEWAL_GRACE_MS + 5_000;
/** The row a user rests on this long is told to the room: moving down a grid tells the last one. */
const FOCUS_DELAY_MS = 300;

/** Who else is in a room, as the room lets this user see them (ADR-279). */
export interface LivePresence {
  present: readonly PresentPerson[];
  more: number;
}

/** Nobody, or nobody known: no socket, or the room has not told yet. */
export const NOBODY_HERE: LivePresence = Object.freeze({ present: Object.freeze([]), more: 0 });

/**
 * The last change of a room by someone else whom the room named (ADR-308): who, when it arrived (epoch ms), and a
 * number that grows with each, so a screen tells one change from the next.
 */
export interface RemoteChange {
  by: LiveChangedBy;
  at: number;
  seq: number;
}

interface Grant {
  token: string;
  claims: LiveGrantClaims;
  capturedAt: number;
}

interface Room {
  listeners: Set<RoomListener>;
  socket: LiveSocket | null;
  live: boolean;
  /** Attempts since a socket of the room last lived `STABLE_MS`: the backoff grows with them. */
  failures: number;
  /** When the room last admitted a socket (epoch ms). */
  admittedAt: number;
  /** The socket was refused or did not open, and the screens read again: the room waits for the grant of that read. */
  waiting: boolean;
  retry?: ReturnType<typeof setTimeout> | undefined;
  /** The socket opened: it is admitted if it is still open after `ADMITTED_AFTER_MS`. */
  admit?: ReturnType<typeof setTimeout> | undefined;
  ping?: ReturnType<typeof setInterval> | undefined;
  pong?: ReturnType<typeof setTimeout> | undefined;
  change?: ReturnType<typeof setTimeout> | undefined;
  /** The change waiting to be read was signalled by the room. */
  signalled: boolean;
  /** The polling of reserve, from the first time the socket was lost until it opens again. */
  reserve?: ReturnType<typeof setInterval> | undefined;
  /** The screens of the tab that show who is here (`join`): while there are any, the room knows where the user is. */
  here: number;
  /** The row the room was last told (`here`), or undefined while it does not know this socket is here. */
  told?: string | null | undefined;
  focusing?: ReturnType<typeof setTimeout> | undefined;
  /** When the room was last told the user is typing (epoch ms). */
  typedAt: number;
  presence: LivePresence;
  /** "Is typing" ends this long after the room last said it, unless it says it again. */
  typingEnds?: ReturnType<typeof setTimeout> | undefined;
  /** The last change by someone else the room named (ADR-308). */
  lastChange?: RemoteChange | undefined;
  /** Renewing (ADR-310): who was here is forgotten when this ends, unless a socket is admitted or the room tells it. */
  stale?: ReturnType<typeof setTimeout> | undefined;
  /** Renewing in a hidden tab: the read waits until the tab shows again; this stops waiting. */
  unhide?: (() => void) | undefined;
}

/**
 * The rows of a read that came back different from the read before (ADR-308): by reference first, since TanStack
 * Query keeps the object of a row that did not change, then by their JSON, for a screen that rebuilds its rows. Only
 * rows of both reads: a new one may be a page loaded or a filter changed, never surely the change. The ids, in the
 * order of the read.
 */
export function changedRows<T>(before: ReadonlyMap<string, T>, after: ReadonlyMap<string, T>): string[] {
  const changed: string[] = [];
  for (const [id, row] of after) {
    const old = before.get(id);
    if (old !== undefined && old !== row && JSON.stringify(old) !== JSON.stringify(row)) changed.push(id);
  }
  return changed;
}

/** How long after the room names who changed it a read is still taken as that change. */
const CHANGE_WINDOW_MS = 5_000;

/** The rows a screen shows, and when its query last read them (`dataUpdatedAt`, epoch ms). */
export interface RowsRead<T> {
  rows: readonly T[];
  readAt: number;
}

/**
 * What a screen lights after a change someone else made (ADR-308): the first read that ends after the change spends
 * it, whatever it brings, so a later read (the user's own save, a page loaded) is never credited to that change. The
 * rows of both reads that came back different, within `CHANGE_WINDOW_MS`. `spend`: the change is done with.
 */
export function rowsToLight<T>(
  before: RowsRead<T> | null,
  after: RowsRead<T>,
  change: RemoteChange | undefined,
  spent: number,
  getRowId: (row: T) => string,
  now: number,
): { spend: boolean; lit: string[] } {
  if (!change || change.seq === spent || after.readAt <= change.at) return { spend: false, lit: [] };
  if (!before || now - change.at > CHANGE_WINDOW_MS) return { spend: true, lit: [] };
  const byId = (list: readonly T[]) => new Map(list.map((row) => [getRowId(row), row]));
  return { spend: true, lit: changedRows(byId(before.rows), byId(after.rows)) };
}

/** The claims of a grant, read without its signature: the gateway checks that, the browser only routes it. */
export function readLiveGrant(token: string): LiveGrantClaims | null {
  const encoded = token.split(".")[1];
  if (!encoded) return null;
  try {
    const json = atob(encoded.replaceAll("-", "+").replaceAll("_", "/"));
    const claims = liveGrantClaims.safeParse(JSON.parse(json));
    return claims.success ? claims.data : null;
  } catch {
    return null;
  }
}

/**
 * The wait before attempt `failures`: doubling from `base`, up to five minutes, a quarter more at random. Also for
 * another socket that lives by the grants of the rooms (the assistant, ADR-284).
 */
export const backoff = (base: number, failures: number): number =>
  Math.min(base * 2 ** Math.max(failures - 1, 0), MAX_DELAY_MS) * (1 + Math.random() / 4);

export function createLiveClient(connect: LiveConnect) {
  const grants = new Map<string, Grant>();
  const rooms = new Map<string, Room>();
  const ownWrites = new Set<string>();
  const statusListeners = new Set<() => void>();
  const presenceListeners = new Set<() => void>();
  const changeListeners = new Set<() => void>();
  let changes = 0;
  /** The row the user is on in each room, as its grid last said: kept before the room is joined too. */
  const focused = new Map<string, string | null>();

  const notifyStatus = () => {
    for (const listener of statusListeners) listener();
  };

  const grantOf = (room: string): Grant | undefined => {
    const grant = grants.get(room);
    return grant && grant.claims.expiresAt - Date.now() > GRANT_SLACK_MS ? grant : undefined;
  };

  const readAgain = (room: Room, signalled: boolean) => {
    for (const listener of room.listeners) listener.changed(signalled);
  };

  const scheduleChange = (room: Room, signalled: boolean) => {
    room.signalled ||= signalled;
    room.change ??= setTimeout(() => {
      room.change = undefined;
      readAgain(room, room.signalled);
      room.signalled = false;
    }, COALESCE_MS);
  };

  const stopTimers = (room: Room) => {
    clearTimeout(room.retry);
    clearTimeout(room.admit);
    clearInterval(room.ping);
    clearTimeout(room.pong);
    clearTimeout(room.stale);
    room.unhide?.();
    room.retry = room.admit = room.ping = room.pong = room.stale = undefined;
  };

  /** The presence kept across a renewal is the room's again: from the socket admitted, or from what it tells. */
  const keepPresence = (room: Room) => {
    clearTimeout(room.stale);
    room.stale = undefined;
  };

  /** `run` now, or when the tab shows again: a hidden tab does not read (ADR-310, a renewal). */
  const whenVisible = (room: Room, run: () => void) => {
    const doc = globalThis.document;
    if (!doc?.hidden) return run();
    const shown = () => {
      if (doc.hidden) return;
      room.unhide?.();
      run();
    };
    doc.addEventListener("visibilitychange", shown);
    room.unhide = () => {
      doc.removeEventListener("visibilitychange", shown);
      room.unhide = undefined;
    };
  };

  const stopReserve = (room: Room) => {
    clearInterval(room.reserve);
    room.reserve = undefined;
  };

  const setPresence = (room: Room, presence: LivePresence) => {
    clearTimeout(room.typingEnds);
    room.typingEnds = undefined;
    room.presence = presence;
    if (presence.present.some((person) => person.typing)) {
      room.typingEnds = setTimeout(() => {
        room.typingEnds = undefined;
        room.presence = { ...presence, present: presence.present.map((person) => ({ ...person, typing: false })) };
        for (const listener of presenceListeners) listener();
      }, TYPING_MS);
    }
    for (const listener of presenceListeners) listener();
  };

  const say = (socket: LiveSocket, message: LiveClientMessage) => socket.send(JSON.stringify(message));

  /**
   * The room hears where the user is, if a screen of the tab shows who is here and the room does not know it yet. A
   * row that is not an id the room takes is told as none: the room would ignore the whole message.
   */
  const tellHere = (name: string, room: Room) => {
    const focus = focused.get(name) ?? null;
    const row = focus !== null && liveRowId.safeParse(focus).success ? focus : null;
    if (!room.live || !room.socket || room.here === 0 || room.told === row) return;
    say(room.socket, { type: "here", row });
    room.told = row;
  };

  /** The socket is gone: the room forgets where this tab was, and hears it again from the next socket. */
  const forgetTold = (room: Room) => {
    clearTimeout(room.focusing);
    room.focusing = undefined;
    room.told = undefined;
  };

  /** The socket is gone: the room forgets this tab, and the tab who was there. */
  const forgetPresence = (room: Room) => {
    forgetTold(room);
    if (room.presence !== NOBODY_HERE) setPresence(room, NOBODY_HERE);
  };

  /**
   * `catchUp`: a change may have come before this socket (it dropped, or the read is not recent). `grant`: null for
   * the user's own room, which the session opens.
   */
  function open(name: string, room: Room, grant: Grant | null, catchUp: boolean): void {
    room.waiting = false;
    let admitted = false;
    const protocols = grant ? [LIVE_PROTOCOL, `${LIVE_GRANT_PROTOCOL_PREFIX}${grant.token}`] : [LIVE_PROTOCOL];
    const socket = connect(name, protocols, {
      open() {
        if (room.socket !== socket) return;
        // a room refuses by answering 101 with the socket already closed (ADR-277, point 7): only a socket still open
        // a moment later is admitted, and only then the screens stop polling
        room.admit = setTimeout(() => {
          room.admit = undefined;
          admitted = true;
          room.live = true;
          room.admittedAt = Date.now();
          keepPresence(room);
          stopReserve(room);
          room.ping = setInterval(() => {
            socket.send("ping");
            room.pong ??= setTimeout(() => dropped(name, room, socket, true), PONG_WITHIN_MS);
          }, PING_EVERY_MS);
          notifyStatus();
          tellHere(name, room);
          if (catchUp) scheduleChange(room, false);
        }, ADMITTED_AFTER_MS);
      },
      message(data) {
        if (room.socket !== socket) return;
        clearTimeout(room.pong);
        room.pong = undefined;
        if (data === "pong" || typeof data !== "string") return;
        const message = liveMessage.safeParse(parseJson(data));
        if (!message.success) return;
        if (message.data.type === "notified") {
          if (name === OWN_ROOM && !ownWrites.has(message.data.requestId)) scheduleChange(room, true);
          return;
        }
        if (message.data.room !== name) return;
        if (message.data.type === "presence") {
          const { present, more } = message.data;
          keepPresence(room);
          return setPresence(room, { present, more });
        }
        const own = ownWrites.has(message.data.requestId);
        // only the last change, if someone it names made it: one of this tab's, or a nameless one, credits nobody
        room.lastChange = !own && message.data.by ? { by: message.data.by, at: Date.now(), seq: ++changes } : undefined;
        for (const listener of changeListeners) listener();
        if (!own) scheduleChange(room, true);
      },
      close(code) {
        if (room.socket !== socket) return;
        dropped(name, room, socket, admitted && code !== liveCloseCodes.revoked, code === liveCloseCodes.grantEnded);
      },
    });
    room.socket = socket;
  }

  /**
   * The socket of a room is gone. `wasLive`: it was admitted and dropped, and is opened again soon with the same grant.
   * Otherwise the upgrade was refused or never answered, or the room closed it with `4403`, or the socket opened again
   * after a drop failed too: after the backoff the screens read again, and the room opens with the grant of that read,
   * never with one read during the wait, which may still be inside the margin of a revocation. The user's own room
   * opens again after the backoff with the session: the gateway checks it at every upgrade. `renewing`: the room closed
   * it at the end of its grant (ADR-310), which is a drop whose grant has ended: the screens read again for a new one,
   * once the tab is visible, and keep showing who was here until the room tells it again, or for `RENEWAL_PRESENCE_MS`
   * when no socket comes back.
   */
  function dropped(name: string, room: Room, socket: LiveSocket, wasLive: boolean, renewing = false): void {
    stopTimers(room);
    room.socket = null;
    socket.close();
    if (renewing) {
      forgetTold(room);
      room.stale = setTimeout(() => {
        room.stale = undefined;
        forgetPresence(room);
      }, RENEWAL_PRESENCE_MS);
    } else forgetPresence(room);
    // only a socket that lived starts the backoff again: one that keeps dropping right after it is admitted backs off
    if (wasLive && Date.now() - room.admittedAt >= STABLE_MS) room.failures = 0;
    room.failures += 1;
    if (room.live) {
      room.live = false;
      notifyStatus();
    }
    // a hidden tab does not read: when it shows again, the next tick does
    room.reserve ??= setInterval(() => {
      if (!globalThis.document?.hidden) readAgain(room, false);
    }, LIVE_RESERVE_POLL_MS);
    const reuse = wasLive && room.failures === 1;
    room.retry = setTimeout(
      () => {
        room.retry = undefined;
        if (name === OWN_ROOM) return open(name, room, null, true);
        const grant = reuse ? grantOf(name) : undefined;
        if (grant) return open(name, room, grant, true);
        const read = () => {
          grants.delete(name);
          room.waiting = true;
          readAgain(room, false);
        };
        if (renewing) whenVisible(room, read);
        else read();
      },
      !reuse
        ? backoff(REFUSED_DELAY_MS, room.failures)
        : name === OWN_ROOM
          ? DROP_DELAY_MS + Math.random() * OWN_ROOM_SPREAD_MS
          : backoff(DROP_DELAY_MS, 1),
    );
  }

  function close(name: string, room: Room): void {
    stopTimers(room);
    stopReserve(room);
    clearTimeout(room.change);
    room.socket?.close();
    room.socket = null;
    forgetPresence(room);
    rooms.delete(name);
    focused.delete(name);
    if (room.live) {
      room.live = false;
      notifyStatus();
    }
  }

  /**
   * A screen listens to a room: the first one opens its socket, with the grant its read gave; without one (the
   * gateway gives none: `LIVE` off) the room waits for the next read that gives it. The user's own room opens at once,
   * and with `LIVE` off it is refused, then tried again after the backoff. Returns the unsubscribe.
   */
  function listen(name: string, listener: RoomListener): () => void {
    let room = rooms.get(name);
    if (!room) {
      room = {
        listeners: new Set(),
        socket: null,
        live: false,
        failures: 0,
        admittedAt: 0,
        waiting: true,
        signalled: false,
        here: 0,
        typedAt: 0,
        presence: NOBODY_HERE,
      };
      rooms.set(name, room);
      // a grant of a read that is not recent (a page from the cache) may have missed a change
      const grant = grantOf(name);
      if (name === OWN_ROOM) open(name, room, null, false);
      else if (grant) open(name, room, grant, Date.now() - grant.capturedAt > CATCH_UP_AFTER_MS);
    }
    room.listeners.add(listener);
    const current = room;
    return () => {
      current.listeners.delete(listener);
      if (current.listeners.size > 0 || rooms.get(name) !== current) return;
      close(name, current);
    };
  }

  return {
    /** A grant a read answered with (`onLiveGrant` of the api client): a room waiting for one opens with it. */
    captureGrant(token: string): void {
      const claims = readLiveGrant(token);
      if (!claims) return;
      grants.set(claims.room, { token, claims, capturedAt: Date.now() });
      const room = rooms.get(claims.room);
      const grant = grantOf(claims.room);
      if (room?.waiting && !room.socket && !room.retry && grant) open(claims.room, room, grant, false);
    },

    /**
     * The grant of `room` still good for a while, as the last read gave it: what another socket of the same room offers
     * (the assistant, ADR-284). Undefined when there is none: the screen reads its object again for a new one.
     */
    grantFor(room: string): string | undefined {
      return grantOf(room)?.token;
    },

    /** A write of this tab (`onWrite` of the api client): its own signal is not read again. */
    rememberWrite(requestId: string): void {
      ownWrites.add(requestId);
      if (ownWrites.size > OWN_WRITES) ownWrites.delete(ownWrites.values().next().value as string);
      // what this tab reads after its own write is not someone else's change (ADR-308)
      for (const room of rooms.values()) room.lastChange = undefined;
      for (const listener of changeListeners) listener();
    },

    listen,

    /**
     * A screen shows who is here (ADR-279): it listens to the room, and while it does the room knows where the user
     * is (`focus`) and tells who else is there (`presenceOf`). Returns the leave.
     */
    join(name: string): () => void {
      const unlisten = listen(name, { changed: () => {} });
      const room = rooms.get(name) as Room;
      room.here += 1;
      tellHere(name, room);
      return () => {
        room.here -= 1;
        if (room.here === 0 && room.told !== undefined && rooms.get(name) === room) {
          if (room.socket) say(room.socket, { type: "away" });
          room.told = undefined;
        }
        unlisten();
      };
    },

    /** The user is on `row` of the room's grid (null: on none): the room hears the row they rest on. */
    focus(name: string, row: string | null): void {
      focused.set(name, row);
      const room = rooms.get(name);
      if (!room) return;
      clearTimeout(room.focusing);
      room.focusing = setTimeout(() => {
        room.focusing = undefined;
        tellHere(name, room);
      }, FOCUS_DELAY_MS);
    },

    /** The user is typing in the room's screen: the room hears it at most every `TYPING_EVERY_MS`. */
    typing(name: string): void {
      const room = rooms.get(name);
      if (!room?.live || !room.socket || room.here === 0 || Date.now() - room.typedAt < TYPING_EVERY_MS) return;
      tellHere(name, room);
      say(room.socket, { type: "typing" });
      room.typedAt = Date.now();
    },

    /** Who else is in the room: nobody while its socket is not open. */
    presenceOf: (name: string): LivePresence => rooms.get(name)?.presence ?? NOBODY_HERE,

    /** Called whenever the presence of a room changes (`useSyncExternalStore`). */
    onPresence(listener: () => void): () => void {
      presenceListeners.add(listener);
      return () => presenceListeners.delete(listener);
    },

    /** The last change of the room by someone else whom the room named (ADR-308), if any. */
    changeOf: (name: string): RemoteChange | undefined => rooms.get(name)?.lastChange,

    /** Called whenever a room hears of a change by someone it names (`useSyncExternalStore`). */
    onChange(listener: () => void): () => void {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },

    /** Whether the socket of the room is open: then the screens of the room do not poll. */
    isLive: (name: string): boolean => rooms.get(name)?.live ?? false,

    /** Called whenever a room opens or loses its socket (`useSyncExternalStore`). */
    onStatus(listener: () => void): () => void {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener);
    },

    /** Every socket closed: the session ends (the app leaves with a full load, which closes them too). */
    stop(): void {
      for (const [name, room] of rooms) close(name, room);
    },
  };
}

export type LiveClient = ReturnType<typeof createLiveClient>;

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** The browser's socket of a room, on the app's origin: the app worker forwards `/api/live/*` to the gateway. */
export const browserConnect: LiveConnect = (room, protocols, on) => {
  const path = room === OWN_ROOM ? "/api/live/me" : `/api/live/rooms/${encodeURIComponent(room)}`;
  const url = new URL(path, location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(url, protocols);
  socket.addEventListener("open", () => on.open());
  socket.addEventListener("message", (event) => on.message(event.data));
  socket.addEventListener("close", (event) => on.close(event.code));
  return {
    send: (data) => {
      if (socket.readyState === WebSocket.OPEN) socket.send(data);
    },
    close: () => socket.close(),
  };
};
